import { Injectable, inject } from '@angular/core';
import { SubscriptionApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/subscription/subscription-api';
import { WebViewBridgeService } from '../webview/webview-bridge.service';
import { WebViewAction } from '../../enums/webview-action';
import { PosthogService } from './posthog.service';

export type CheckoutPlan = 'MONTHLY' | 'YEARLY';

/**
 * Contrat d'un moyen de paiement.
 *
 * Cette interface existe pour une raison précise : si Apple ou Google exigent
 * un jour l'achat in-app, on écrit un `IapCheckoutProvider` qui l'implémente et
 * AUCUN écran ne change. Le bouton d'achat, lui, vit dans l'app web : on peut
 * donc basculer sans passer par une release des stores.
 */
export interface CheckoutProvider {
  /** Ouvre le tunnel d'achat. Résout quand le tunnel est OUVERT, pas payé. */
  purchase(plan: CheckoutPlan): Promise<void>;
  /** Factures et moyen de paiement. */
  manageBilling(): Promise<void>;
  /** Ce moyen de paiement est-il utilisable dans le contexte courant ? */
  isAvailable(): boolean;
}

/**
 * Paiement par Stripe, ouvert dans le NAVIGATEUR EXTERNE.
 *
 * La transaction ne se déroule jamais dans la WebView : c'est la voie conforme
 * aux règles des stores (modèle Spotify), et elle évite d'avoir à reverser une
 * commission.
 */
@Injectable({ providedIn: 'root' })
export class CheckoutService implements CheckoutProvider {
  private readonly subscriptionApi = new SubscriptionApi();
  private readonly webViewBridge = inject(WebViewBridgeService);
  private readonly posthog = inject(PosthogService);

  /** Un achat a été lancé : sert à resynchroniser au retour dans l'app. */
  private purchaseInFlight = false;

  isAvailable(): boolean {
    return true;
  }

  async purchase(plan: CheckoutPlan): Promise<void> {
    // Les navigateurs mobiles n'autorisent `window.open` que pendant la partie
    // SYNCHRONE du geste utilisateur : on pré-ouvre l'onglet AVANT tout await,
    // et on y pose l'URL ensuite. Sans cela, le bloqueur de fenêtres surgissantes
    // avale le tunnel de paiement.
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');

    try {
      const { url } = await this.subscriptionApi.createCheckoutSession(plan);
      this.purchaseInFlight = true;
      this.posthog.capture('checkout_started', { plan });
      this.openExternal(url, pendingTab);
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  async manageBilling(): Promise<void> {
    const pendingTab = this.hasNativeChannel()
      ? null
      : window.open('', '_blank');
    try {
      const { url } = await this.subscriptionApi.createPortalSession();
      this.openExternal(url, pendingTab);
    } catch (error) {
      pendingTab?.close();
      throw error;
    }
  }

  /** Un achat vient-il d'être lancé ? (consommé une seule fois) */
  consumePurchaseInFlight(): boolean {
    const value = this.purchaseInFlight;
    this.purchaseInFlight = false;
    return value;
  }

  private hasNativeChannel(): boolean {
    return (
      this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl) ||
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    );
  }

  /**
   * Ouvre dans le navigateur EXTERNE. Cascade identique à celle du renvoi vers
   * la boutique : canal dédié → canal OAuth (indispensable, le build actuel des
   * stores ne connaît pas encore `openExternalUrl`) → onglet pré-ouvert → dernier
   * recours.
   */
  private openExternal(url: string, pendingTab: Window | null): void {
    if (this.webViewBridge.isChannelAvailable(WebViewAction.openExternalUrl)) {
      this.webViewBridge.postMessageToChannel(
        WebViewAction.openExternalUrl,
        url,
      );
    } else if (
      this.webViewBridge.isChannelAvailable(WebViewAction.openOAuthUrl)
    ) {
      this.webViewBridge.postMessageToChannel(WebViewAction.openOAuthUrl, url);
    } else if (pendingTab && !pendingTab.closed) {
      pendingTab.location.href = url;
    } else {
      window.open(url, '_blank');
    }
  }
}
